/**
 * BulkExtract - Extraction IA en masse depuis plusieurs URLs / textes
 * Crée automatiquement des concours en brouillon
 */
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
  XCircle,
} from 'lucide-react';
import React, { useState } from 'react';
import { authFetch } from '../../services/api';

interface BulkExtractProps {
  onBack: () => void;
  onDraftsCreated: () => void;
}

interface BulkItem {
  id: number;
  url: string;
  label: string;
}

interface BulkResult {
  source: string;
  success: boolean;
  action?: string;
  slug?: string;
  title?: string;
  error?: string;
}

let _id = 0;
const nextId = () => ++_id;

const BulkExtract: React.FC<BulkExtractProps> = ({ onBack, onDraftsCreated }) => {
  const [items, setItems] = useState<BulkItem[]>([
    { id: nextId(), url: '', label: '' },
    { id: nextId(), url: '', label: '' },
  ]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<BulkResult[] | null>(null);
  const [summary, setSummary] = useState<{ total: number; created: number; failed: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const addRow = () => {
    if (items.length >= 20) return;
    setItems(prev => [...prev, { id: nextId(), url: '', label: '' }]);
  };

  const removeRow = (id: number) => {
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const updateRow = (id: number, field: 'url' | 'label', value: string) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  const handleExtract = async () => {
    const validItems = items.filter(i => i.url.trim());
    if (validItems.length === 0) {
      setError('Ajoutez au moins une URL.');
      return;
    }
    setError(null);
    setResults(null);
    setSummary(null);
    setLoading(true);

    try {
      const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const res = await authFetch(`${apiBase}/api/pka/bulk-extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: validItems.map(i => ({
            url: i.url.trim(),
            label: i.label.trim() || undefined,
          })),
          save_as_draft: true,
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || `Erreur ${res.status}`);
      setResults(body.data.results);
      setSummary({ total: body.data.total, created: body.data.created, failed: body.data.failed });
      if (body.data.created > 0) onDraftsCreated();
    } catch (err: any) {
      setError(err.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-violet-600" />
            Extraction IA en masse
          </h1>
          <p className="text-gray-600 text-sm mt-0.5">
            Collez jusqu'à 20 URLs — chaque concours sera extrait et sauvegardé en brouillon automatiquement.
          </p>
        </div>
      </div>

      {/* URL list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-3">
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-medium text-gray-500 px-1">
          <span>URL (page web ou PDF)</span>
          <span>Libellé optionnel</span>
          <span />
        </div>

        {items.map((item, idx) => (
          <div key={item.id} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
            <div className="relative">
              <ExternalLink className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="url"
                value={item.url}
                onChange={e => updateRow(item.id, 'url', e.target.value)}
                placeholder={`https://... (${idx + 1})`}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
            <input
              type="text"
              value={item.label}
              onChange={e => updateRow(item.id, 'label', e.target.value)}
              placeholder="ex: CAFOP 2026"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
            <button
              type="button"
              onClick={() => removeRow(item.id)}
              disabled={items.length <= 1}
              className="p-2 text-gray-400 hover:text-red-500 disabled:opacity-30 transition"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={addRow}
          disabled={items.length >= 20}
          className="flex items-center gap-1.5 text-sm text-violet-600 hover:text-violet-800 disabled:opacity-40 transition mt-1"
        >
          <Plus className="w-4 h-4" />
          Ajouter une URL ({items.length}/20)
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Action */}
      {!results && (
        <div className="flex justify-end">
          <button
            onClick={handleExtract}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white rounded-xl font-medium transition"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
            {loading ? 'Extraction en cours…' : `Extraire et créer ${items.filter(i => i.url.trim()).length} brouillon(s)`}
          </button>
        </div>
      )}

      {/* Results */}
      {results && summary && (
        <div className="space-y-4">
          {/* Summary banner */}
          <div className={`rounded-lg p-4 flex items-center justify-between ${summary.failed === 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
            }`}>
            <div className="text-sm font-medium">
              <span className="text-green-700">{summary.created} brouillon(s) créé(s)</span>
              {summary.failed > 0 && (
                <span className="text-red-600 ml-3">{summary.failed} erreur(s)</span>
              )}
            </div>
            <button
              onClick={() => { setResults(null); setSummary(null); }}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Nouvelle extraction
            </button>
          </div>

          {/* Per-item results */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
            {results.map((r, i) => (
              <div key={i} className="flex items-start gap-3 p-4">
                {r.success
                  ? <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  : <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {r.title || r.source}
                  </p>
                  {r.success ? (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Brouillon créé — slug : <code className="font-mono">{r.slug}</code>
                    </p>
                  ) : (
                    <p className="text-xs text-red-600 mt-0.5">{r.error}</p>
                  )}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${r.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                  {r.success ? 'OK' : 'Erreur'}
                </span>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
            >
              Voir les brouillons
            </button>
          </div>
        </div>
      )}

      {/* Loading overlay message */}
      {loading && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 text-sm text-violet-700 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          Analyse IA en cours — chaque URL est traitée séquentiellement. Cela peut prendre quelques secondes par URL…
        </div>
      )}
    </div>
  );
};

export default BulkExtract;
